import React from 'react';
import { Box } from '@mui/material';
import {
  signalAvatarTones,
  signalBorders,
  signalColors,
  signalDawLogos,
  signalFonts,
  signalRadii,
  signalShadows,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  DawTile,
  GlassCard,
  InitialsAvatar,
  Mono,
  ScreenFrame,
  SectionLabel,
  SignalButton,
  StatusPill,
} from '@/common/components/signal';

const ROLES = [
  { name: 'COLLABORATOR', blurb: 'Push versions, download, comment.' },
  {
    name: 'VIEWER',
    blurb: 'Stream renders and read history. No downloads.',
  },
  { name: 'OWNER', blurb: 'Everything, including delete and transfer.' },
];

type Access = {
  initials: string;
  bg: string;
  fg: string;
  name: string;
  sub: string;
  /** Role label, or a PENDING pill for someone who hasn't accepted yet. */
  role?: string;
  /** The owner's role is accent-coloured; everyone else's is muted. */
  owner?: boolean;
  pending?: boolean;
};

const access: Access[] = [
  {
    initials: 'MK',
    ...signalAvatarTones.accent,
    name: 'Mara Kade',
    sub: 'mara@autocannon.fm',
    role: 'OWNER',
    owner: true,
  },
  {
    initials: 'JD',
    ...signalAvatarTones.cyan,
    name: 'Jonah Dey',
    sub: 'jonah@dey.audio',
    role: 'COLLABORATOR ▾',
  },
  {
    initials: 'RN',
    ...signalAvatarTones.muted,
    name: 'rae@northlane.co',
    sub: 'Invited 2 days ago',
    pending: true,
  },
];

/**
 * 08 — Share & invite.
 *
 * The role picker is shown open, which is the point of the screen: the three
 * roles are indistinguishable as bare labels, so each carries a one-line
 * statement of what it actually permits. Deciding between COLLABORATOR and
 * VIEWER shouldn't require leaving the dialog to find documentation.
 */
export const Screen08Share: React.FC = () => (
  <ScreenFrame
    index="08"
    title="Share & invite"
    note="DIALOG · ROLE PICKER OPEN"
    bare
  >
    <Box
      sx={{
        display: 'flex',
        gap: '24px',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
      }}
    >
      <GlassCard
        width={620}
        padding="28px"
        sx={{ background: signalSurfaces.glassStrong }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            marginBottom: '20px',
          }}
        >
          <DawTile
            src={signalDawLogos.Ableton}
            label="Ableton"
            size={38}
            radius={10}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              component="h3"
              sx={{
                margin: 0,
                fontFamily: signalFonts.display,
                fontWeight: 800,
                fontSize: '24px',
                letterSpacing: '-0.03em',
              }}
            >
              Share Nightdrive
            </Box>
            <Mono size={10.5} sx={{ display: 'block', marginTop: '5px' }}>
              ABLETON LIVE · v14 · 1.84 GB
            </Mono>
          </Box>
          <Box
            component="span"
            sx={{ color: signalText.muted, fontSize: '13px' }}
          >
            ✕
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '9px',
            alignItems: 'center',
            marginBottom: '8px',
          }}
        >
          {/* Focused email field, mid-typing. */}
          <Box
            sx={{
              flex: 1,
              background: 'rgba(255,106,31,0.06)',
              border: `1px solid ${signalColors.accent}`,
              borderRadius: `${signalRadii.md}px`,
              padding: '11px 14px',
              boxShadow: signalShadows.focusRing,
              fontSize: '14px',
            }}
          >
            rae@
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: signalSurfaces.fillHover,
              border: `1px solid ${signalBorders.control}`,
              borderRadius: `${signalRadii.md}px`,
              padding: '11px 13px',
              fontFamily: signalFonts.mono,
              fontWeight: 600,
              fontSize: '11px',
              cursor: 'pointer',
            }}
          >
            COLLABORATOR
            <Box component="span" sx={{ color: signalText.muted }}>
              ▾
            </Box>
          </Box>
          <SignalButton glow={false} sx={{ padding: '11px 17px' }}>
            Invite
          </SignalButton>
        </Box>

        <Box
          sx={{
            width: 340,
            border: `1px solid ${signalBorders.stronger}`,
            borderRadius: `${signalRadii.lg}px`,
            background: signalSurfaces.glassMenu,
            boxShadow: signalShadows.menu,
            padding: '6px',
            marginBottom: '6px',
          }}
        >
          {ROLES.map((role, i) => (
            <Box
              key={role.name}
              sx={{
                display: 'flex',
                gap: '10px',
                padding: '10px',
                borderRadius: '9px',
                cursor: 'pointer',
                background: i === 0 ? signalSurfaces.fillStrong : 'transparent',
                '&:hover':
                  i === 0
                    ? undefined
                    : { background: signalSurfaces.fillStrong },
              }}
            >
              {/* Transparent tick on unselected rows keeps all three labels
                  on one optical left edge. */}
              <Box
                component="span"
                sx={{
                  color: i === 0 ? signalColors.accentBright : 'transparent',
                  fontSize: '11px',
                  paddingTop: '2px',
                }}
              >
                ✓
              </Box>
              <Box>
                <Mono size={11} weight={600} color={signalText.primary}>
                  {role.name}
                </Mono>
                <Box
                  sx={{
                    fontSize: '11.5px',
                    color: signalText.secondary,
                    marginTop: '3px',
                    lineHeight: 1.45,
                  }}
                >
                  {role.blurb}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        <SectionLabel
          size={10}
          sx={{ display: 'block', marginTop: '18px', marginBottom: '12px' }}
        >
          PEOPLE WITH ACCESS
        </SectionLabel>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {access.map((person) => (
            <Box
              key={person.initials}
              sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}
            >
              <InitialsAvatar
                initials={person.initials}
                bg={person.bg}
                fg={person.fg}
                size={28}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: '13px',
                    color: person.pending
                      ? signalText.secondary
                      : signalText.primary,
                  }}
                >
                  {person.name}
                </Box>
                <Box sx={{ fontSize: '11.5px', color: signalText.muted }}>
                  {person.sub}
                </Box>
              </Box>

              {person.role && (
                <Mono
                  size={10}
                  weight={600}
                  color={
                    person.owner
                      ? signalColors.accentBright
                      : signalText.secondary
                  }
                >
                  {person.role}
                </Mono>
              )}
              {person.pending && (
                <StatusPill status="behind">PENDING</StatusPill>
              )}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            marginTop: '22px',
            paddingTop: '18px',
            borderTop: `1px solid ${signalBorders.subtle}`,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Box
            sx={{
              flex: 1,
              fontSize: '12.5px',
              color: signalText.secondary,
              lineHeight: 1.5,
            }}
          >
            Anyone with the link can{' '}
            <Box component="b" sx={{ color: signalText.primary }}>
              stream renders
            </Box>{' '}
            — no vault access.
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: signalSurfaces.fillStrong,
              border: `1px solid ${signalBorders.strong}`,
              borderRadius: `${signalRadii.md}px`,
              padding: '9px 12px',
              cursor: 'pointer',
            }}
          >
            <Mono size={11} color={signalText.secondary}>
              stv.link/ndrv-14
            </Mono>
            <Mono size={11} weight={600} color={signalColors.accentBright}>
              COPY
            </Mono>
          </Box>
        </Box>
      </GlassCard>

      <Box
        sx={{
          width: 400,
          maxWidth: '100%',
          borderRadius: `${signalRadii['3xl']}px`,
          border: `1px solid ${signalBorders.medium}`,
          background: signalSurfaces.panel,
          boxShadow: signalShadows.screen,
          padding: '24px',
        }}
      >
        <SectionLabel size={10} sx={{ display: 'block', marginBottom: '16px' }}>
          INVITES · INBOX
        </SectionLabel>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Box
            sx={{
              border: '1px solid rgba(255,106,31,0.35)',
              background: 'rgba(255,106,31,0.06)',
              borderRadius: `${signalRadii.xl}px`,
              padding: '15px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '10px',
              }}
            >
              <InitialsAvatar
                initials="JD"
                {...signalAvatarTones.cyan}
                size={26}
              />
              <Box sx={{ fontSize: '13px' }}>
                <b>Jonah Dey</b> invited you
              </Box>
            </Box>
            <Box
              sx={{
                fontSize: '12.5px',
                color: signalText.secondary,
                lineHeight: 1.5,
                marginBottom: '12px',
              }}
            >
              Coastline — Logic Pro · 704 MB · as{' '}
              <Box component="b" sx={{ color: signalText.primary }}>
                Collaborator
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <SignalButton
                glow={false}
                sx={{ flex: 1, padding: '9px', fontSize: '12.5px' }}
              >
                Accept
              </SignalButton>
              <SignalButton
                variant="secondary"
                sx={{
                  flex: 1,
                  padding: '9px',
                  fontSize: '12.5px',
                  background: 'transparent',
                  color: signalText.secondary,
                  borderColor: signalBorders.stronger,
                }}
              >
                Decline
              </SignalButton>
            </Box>
          </Box>

          {/* Already answered — dimmed rather than removed, so the inbox keeps
              a short record of what was decided. */}
          <Box
            sx={{
              border: `1px solid ${signalBorders.default}`,
              borderRadius: `${signalRadii.xl}px`,
              padding: '15px',
              opacity: 0.75,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '8px',
              }}
            >
              <InitialsAvatar
                initials="AR"
                {...signalAvatarTones.green}
                size={26}
              />
              <Box sx={{ fontSize: '13px', color: signalText.secondary }}>
                <Box component="b" sx={{ color: signalText.primary }}>
                  Ada Rein
                </Box>{' '}
                · board invite
              </Box>
            </Box>
            <Box
              sx={{
                fontSize: '12.5px',
                color: signalText.muted,
                lineHeight: 1.5,
              }}
            >
              Sync / Licensing queue · accepted yesterday
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  </ScreenFrame>
);

export default Screen08Share;
